import { normalizeDocumentTemplateIncomeTypes } from './documentTemplateIncomeTypes'
import { normalizeDocumentTemplateLoanTypes } from './documentTemplateLoanTypes'

const DOCUMENT_TEMPLATES_ENDPOINT = '/los/documenttemplates'

export const DOCUMENT_TEMPLATE_STATUS = {
    ACTIVE: 'active',
    INACTIVE: 'inactive',
    ALL: 'all',
}

export const DOCUMENT_TEMPLATE_SORT = {
    UPDATED_DESC: 'updated_desc',
    NAME_ASC: 'name_asc',
    LAST_USED_DESC: 'last_used_desc',
    LAST_USED_ASC: 'last_used_asc',
}

const getHeaders = (hasBody) => {
    const headers = {
        Authorization: `Bearer ${sessionStorage.getItem('ZeitroA') || ''}`,
        Cache: 'no-cache',
    }
    if (hasBody) {
        headers['Content-Type'] = 'application/json'
    }
    return headers
}

const readResponse = async (response) => {
    const text = await response.text()
    let data = null
    if (text) {
        try {
            data = JSON.parse(text)
        } catch (error) {
            data = null
        }
    }

    if (!response.ok) {
        const message =
            data?.errMessage ||
            data?.error ||
            text ||
            `Request failed with status ${response.status}`
        const requestError = new Error(message)
        requestError.status = response.status
        throw requestError
    }
    return data
}

const request = async (path = '', options = {}) => {
    const hasBody = options.body !== undefined
    const response = await fetch(`${DOCUMENT_TEMPLATES_ENDPOINT}${path}`, {
        ...options,
        headers: {
            ...getHeaders(hasBody),
            ...(options.headers || {}),
        },
        body: hasBody ? JSON.stringify(options.body) : undefined,
    })
    return readResponse(response)
}

export const getLoanOfficerLicenseStates = async ({ signal } = {}) => {
    const response = await fetch('/los/getprofilesetup', {
        cache: 'no-cache',
        method: 'GET',
        headers: getHeaders(false),
        signal,
    })
    const profile = await readResponse(response)
    const states = profile?.LandingPageSetUp?.States
    if (!Array.isArray(states)) return []

    return [...new Set(
        states
            .map((state) => String(state || '').trim().toUpperCase())
            .filter((state) => /^[A-Z]{2}$/.test(state))
    )].sort()
}

const normalizeTemplate = (template = {}) => ({
    ...template,
    id: template.id || '',
    name: template.name || '',
    status: template.status || DOCUMENT_TEMPLATE_STATUS.ACTIVE,
    ownerType: template.ownerType || 'loan_officer',
    isOfficial: Boolean(template.isOfficial),
    canEdit: template.canEdit !== false,
    isDefault: Boolean(template.isDefault),
    loanTypes: normalizeDocumentTemplateLoanTypes(
        Array.isArray(template.loanTypes)
            ? template.loanTypes
            : template.loanType
    ),
    incomeTypes: normalizeDocumentTemplateIncomeTypes(
        Array.isArray(template.incomeTypes)
            ? template.incomeTypes
            : template.incomeType
    ),
    allStates: Boolean(template.allStates),
    licenseStates: Array.isArray(template.licenseStates)
        ? template.licenseStates
        : [],
    defaultStates: Array.isArray(template.defaultStates)
        ? template.defaultStates
        : [],
    items: Array.isArray(template.items) ? template.items : [],
    itemCount: Number(template.itemCount) || 0,
})

export const listDocumentTemplates = async ({
    status = DOCUMENT_TEMPLATE_STATUS.ACTIVE,
    state = '',
    sort = DOCUMENT_TEMPLATE_SORT.UPDATED_DESC,
    signal,
} = {}) => {
    const params = new URLSearchParams()
    if (status) params.set('status', status)
    if (state) params.set('state', state)
    if (sort) params.set('sort', sort)

    const data = await request(`?${params.toString()}`, { signal })
    return Array.isArray(data?.templates)
        ? data.templates.map(normalizeTemplate)
        : []
}

export const getDocumentTemplate = async (templateID, { signal } = {}) =>
    normalizeTemplate(await request(`/${templateID}`, { signal }))

export const createDocumentTemplate = async (template) =>
    normalizeTemplate(
        await request('', {
            method: 'POST',
            body: template,
        })
    )

export const updateDocumentTemplate = async (templateID, template) =>
    normalizeTemplate(
        await request(`/${templateID}`, {
            method: 'PUT',
            body: template,
        })
    )

export const duplicateDocumentTemplate = async (templateID) =>
    normalizeTemplate(
        await request(`/${templateID}/duplicate`, {
            method: 'POST',
        })
    )

export const deactivateDocumentTemplate = async (templateID) =>
    normalizeTemplate(
        await request(`/${templateID}/deactivate`, {
            method: 'POST',
        })
    )

export const reactivateDocumentTemplate = async (templateID) =>
    normalizeTemplate(
        await request(`/${templateID}/reactivate`, {
            method: 'POST',
        })
    )

export const setDocumentTemplateDefault = async (templateID, isDefault) =>
    normalizeTemplate(
        await request(`/${templateID}/default`, {
            method: 'POST',
            body: { isDefault },
        })
    )
